<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <!-- Header Card -->
                <div class="card mb-4">
                    <div class="card-header pb-0">
                        <div class="row align-items-center">
                            <div class="col-md-8">
                                <h5 class="mb-1"><i class="fas fa-clone me-2"></i>Duplicates Detection</h5>
                                <p class="text-sm text-muted mb-0">
                                    A list of pensioners with possible duplicate data. Review and resolve duplicate entries.
                                </p>
                            </div>
                            <div class="col-md-4 text-end">
                                <vsud-badge color="info" variant="gradient" size="lg">
                                    {{ duplicateGroups.length }} Groups Found
                                </vsud-badge>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <!-- Search & Filters -->
                        <div class="row mb-3">
                            <div class="col-md-4">
                                <label class="form-label text-xs mb-1">Search</label>
                                <div class="input-group">
                                    <span class="input-group-text"><i class="fas fa-search"></i></span>
                                    <input type="text" class="form-control form-control-sm" 
                                        v-model="searchQuery"
                                        placeholder="Pensioner name, NIN or BVN">
                                </div>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label text-xs mb-1">Group By</label>
                                <select class="form-control form-control-sm" v-model="groupBy" @change="fetchDuplicates">
                                    <option value="bvn">BVN</option>
                                    <option value="nin">NIN</option>
                                    <option value="account_number">Account Number</option>
                                    <option value="name">Name</option>
                                    <option value="phone">Phone Number</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label class="form-label text-xs mb-1">Filter</label>
                                <select class="form-control form-control-sm" v-model="filterStatus" @change="onFilterChange">
                                    <option value="">All Duplicates</option>
                                    <option value="unresolved">Unresolved</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="dual_pension">Dual Pensioners</option>
                                </select>
                            </div>
                            <div class="col-md-2">
                                <label class="form-label text-xs mb-1">&nbsp;</label>
                                <button class="form-control btn btn-sm bg-gradient-info w-100" @click="fetchDuplicates">
                                    <i class="fas fa-sync-alt me-1"></i> Refresh
                                </button>
                            </div>
                        </div>

                        <!-- Info Alert - Dynamic based on filter -->
                        <div v-if="filterStatus === 'dual_pension'" class="alert alert-success d-flex align-items-center mb-0" role="alert">
                            <i class="fas fa-user-friends me-2"></i>
                            <div class="small">
                                <strong>Dual Pensioners:</strong> These are pensioners with legitimate records in both 
                                State and LGA pension systems. This is valid and not considered a duplicate.
                            </div>
                        </div>
                        <div v-else class="alert alert-info d-flex align-items-center mb-0" role="alert">
                            <i class="fas fa-info-circle me-2"></i>
                            <div class="small">
                                <strong>Note:</strong> Duplicates shown here are actual duplicate entries, 
                                not pensioners with legitimate dual pension (State + LGA). 
                                Use the "Dual Pensioners" filter to view those.
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Loading State -->
                <div class="card" v-if="isLoading">
                    <div class="card-body text-center py-5">
                        <vsud-spinner></vsud-spinner>
                        <p class="mt-3 text-muted">Scanning for duplicates...</p>
                    </div>
                </div>

                <!-- Duplicates List -->
                <div v-else-if="filteredGroups.length > 0">
                    <div class="card mb-3" v-for="(group, index) in filteredGroups" :key="index">
                        <div class="card-header py-3 px-4" :class="getHeaderClass(group)">
                            <div class="row align-items-center">
                                <div class="col-md-6">
                                    <h6 class="mb-0 text-white fw-bold">
                                        <i :class="getHeaderIcon(group)" class="me-2"></i>
                                        {{ group.identifier }}
                                    </h6>
                                    <small class="text-white opacity-8">
                                        {{ group.pensioners.length }} records found | Grouped by: {{ formatGroupBy(groupBy) }}
                                    </small>
                                </div>
                                <div class="col-md-6 text-end">
                                    <span v-if="group.isDualPension" class="badge badge-status badge-dual">
                                        <i class="fas fa-check-circle me-1"></i> Dual Pensioner (Valid)
                                    </span>
                                    <span v-else-if="group.status === 'resolved'" class="badge badge-status badge-resolved">
                                        <i class="fas fa-check me-1"></i> Resolved
                                    </span>
                                    <span v-else class="badge badge-status badge-review">
                                        <i class="fas fa-exclamation-circle me-1"></i> Needs Review
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="card-body p-0">
                            <div class="table-responsive">
                                <table class="table table-hover align-items-center mb-0">
                                    <thead class="bg-light">
                                        <tr>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Pension ID</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Name</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">BVN</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">NIN</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Phone</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Type</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Monthly Pension</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Status</th>
                                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="pensioner in group.pensioners" :key="pensioner.id" 
                                            :class="pensioner.isPrimary ? 'table-success' : ''">
                                            <td>
                                                <span class="text-xs font-weight-bold text-info">
                                                    {{ pensioner.pensionId || '—' }}
                                                </span>
                                                <span v-if="pensioner.isPrimary" class="badge bg-success ms-1">Primary</span>
                                            </td>
<td>
                                            <div class="d-flex align-items-center">
                                                <div class="avatar avatar-sm me-2">
                                                    <img :src="getPhotoUrl(pensioner.photoLink)" class="rounded-circle" @error="handleImageError($event)" />
                                                </div>
                                                    <div>
                                                        <span class="text-xs font-weight-bold">{{ pensioner.firstName }} {{ pensioner.lastName }}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span class="text-xs">{{ pensioner.bvn || '—' }}</span></td>
                                            <td><span class="text-xs">{{ pensioner.nin || '—' }}</span></td>
                                            <td><span class="text-xs">{{ pensioner.phoneNo || '—' }}</span></td>
                                            <td>
                                                <vsud-badge :color="pensioner.isLga == 1 ? 'info' : 'warning'" variant="gradient" size="sm">
                                                    {{ pensioner.isLga == 1 ? 'LGA' : 'State' }}
                                                </vsud-badge>
                                            </td>
                                            <td>
                                                <span class="text-xs font-weight-bold">
                                                    ₦{{ formatCurrency(pensioner.monthlyPension) }}
                                                </span>
                                            </td>
                                            <td>
                                                <vsud-badge :color="pensioner.status === 'completed' ? 'success' : 'danger'" variant="gradient" size="sm">
                                                    {{ pensioner.status === 'completed' ? 'Verified' : 'Not Verified' }}
                                                </vsud-badge>
                                            </td>
                                            <td>
                                                <div class="btn-group">
                                                    <router-link :to="{ name: 'View Pensioner', params: { id: pensioner.id } }" 
                                                        class="btn btn-link text-info p-1" title="View">
                                                        <i class="fas fa-eye"></i>
                                                    </router-link>
                                                    <button v-if="!pensioner.isPrimary && !group.isDualPension" 
                                                        class="btn btn-link text-success p-1" 
                                                        title="Set as Primary"
                                                        @click="setPrimary(group, pensioner)">
                                                        <i class="fas fa-star"></i>
                                                    </button>
                                                    <button v-if="!pensioner.isPrimary && !group.isDualPension" 
                                                        class="btn btn-link text-danger p-1" 
                                                        title="Mark for Removal"
                                                        @click="markForRemoval(pensioner)">
                                                        <i class="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <!-- Group Actions -->
                            <div class="card-footer bg-light py-3 px-4" v-if="!group.isDualPension && group.status !== 'resolved'">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span class="text-xs text-muted">
                                        <i class="fas fa-info-circle me-1"></i>
                                        Select a primary record and mark others for removal to resolve this duplicate.
                                    </span>
                                    <div class="d-flex gap-2">
                                        <button class="btn btn-sm btn-dual-pension" @click="markAsDualPension(group)">
                                            <i class="fas fa-user-friends me-1"></i> Mark as Dual Pension
                                        </button>
                                        <button class="btn btn-sm btn-resolve" @click="resolveGroup(group)"
                                            :disabled="!group.pensioners.some(p => p.isPrimary)">
                                            <i class="fas fa-check me-1"></i> Resolve
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div class="card">
                        <div class="card-body py-2">
                            <div class="d-flex justify-content-between align-items-center">
                                <span class="text-sm text-muted">
                                    Showing {{ filteredGroups.length }} of {{ duplicateGroups.length }} duplicate groups
                                </span>
                                <b-pagination 
                                    v-model="currentPage" 
                                    :total-rows="duplicateGroups.length" 
                                    :per-page="perPage"
                                    aria-controls="duplicates-table">
                                </b-pagination>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Duplicates Found -->
                <div class="card" v-else>
                    <div class="card-body text-center py-5">
                        <i class="fas fa-check-circle text-success fa-4x mb-3"></i>
                        <h5>No Duplicates Found</h5>
                        <p class="text-muted mb-0">
                            Great news! No duplicate records were found based on your search criteria.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Resolution Confirmation Modal -->
        <div class="modal fade" :class="{ 'show d-block': showResolveModal }" tabindex="-1" v-if="showResolveModal">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Resolve Duplicate</h5>
                        <button type="button" class="btn-close" @click="showResolveModal = false"></button>
                    </div>
                    <div class="modal-body">
                        <p>Are you sure you want to resolve this duplicate group?</p>
                        <div class="alert alert-warning">
                            <i class="fas fa-exclamation-triangle me-1"></i>
                            The primary record will be kept, and other records will be marked for review/removal.
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Resolution Note</label>
                            <textarea class="form-control" v-model="resolutionNote" rows="3" 
                                placeholder="Add a note about this resolution..."></textarea>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="showResolveModal = false">Cancel</button>
                        <button type="button" class="btn bg-gradient-success" @click="confirmResolve">
                            <i class="fas fa-check me-1"></i> Confirm Resolution
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop fade show" v-if="showResolveModal" @click="showResolveModal = false"></div>
    </div>
</template>

<script>
import VsudSpinner from "@/components/VsudSpinner.vue";
import VsudBadge from "@/components/VsudBadge.vue";
import AuthService from "@/services/auth.service.js";
import imgDef from "@/assets/img/team-5.png";

export default {
    name: "duplicates-list",
    data() {
        return {
            isLoading: false,
            searchQuery: '',
            groupBy: 'bvn',
            filterStatus: '',
            previousFilter: '', // Track previous filter to know when to refetch
            duplicateGroups: [],
            currentPage: 1,
            perPage: 10,
            imgDef,
            showResolveModal: false,
            selectedGroup: null,
            resolutionNote: '',
            errorMessage: ''
        };
    },
    components: {
        VsudSpinner,
        VsudBadge
    },
    computed: {
        filteredGroups() {
            let groups = this.duplicateGroups;
            
            // Search filter (local filtering)
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                groups = groups.filter(g => 
                    g.identifier.toLowerCase().includes(query) ||
                    g.pensioners.some(p => 
                        (p.firstName && p.firstName.toLowerCase().includes(query)) ||
                        (p.lastName && p.lastName.toLowerCase().includes(query)) ||
                        (p.bvn && p.bvn.includes(query)) ||
                        (p.nin && p.nin.includes(query))
                    )
                );
            }

            // Status filter (local filtering for duplicates, not for duals which are fetched from API)
            if (this.filterStatus !== 'dual_pension') {
                if (this.filterStatus === 'unresolved') {
                    groups = groups.filter(g => g.status !== 'resolved' && !g.isDualPension);
                } else if (this.filterStatus === 'resolved') {
                    groups = groups.filter(g => g.status === 'resolved');
                }
            }
            // When filterStatus is 'dual_pension', data is already fetched from /pensioners/duals API

            return groups;
        }
    },
    created() {
        this.fetchDuplicates();
    },
    methods: {
        /**
         * Fetch data based on current filter
         * - 'dual_pension' filter: calls /pensioners/duals
         * - Other filters: calls /pensioners/duplicates
         */
        async fetchData() {
            this.isLoading = true;
            this.errorMessage = '';
            
            try {
                let response;
                
                if (this.filterStatus === 'dual_pension') {
                    // Fetch dual pensioners from dedicated API
                    console.log('Fetching dual pensioners...');
                    response = await AuthService.getDuals();
                    console.log('Duals API raw response:', response);
                    
                    // Transform duals response
                    if (response.data && Array.isArray(response.data)) {
                        this.duplicateGroups = this.transformDualsData(response.data);
                    } else if (Array.isArray(response)) {
                        this.duplicateGroups = this.transformDualsData(response);
                    } else {
                        this.duplicateGroups = [];
                    }
                } else {
                    // Fetch duplicates
                    const param = `?group_by=${this.groupBy}`;
                    console.log('Fetching duplicates with param:', param);
                    response = await AuthService.getDuplicates(param);
                    console.log('Duplicates API raw response:', response);
                    
                    // Transform duplicates response
                    if (response.data && Array.isArray(response.data)) {
                        this.duplicateGroups = this.transformDuplicatesData(response.data);
                    } else if (Array.isArray(response)) {
                        this.duplicateGroups = this.transformDuplicatesData(response);
                    } else {
                        this.duplicateGroups = [];
                    }
                }
                
                console.log('Final groups:', this.duplicateGroups.length, 'items');
            } catch (error) {
                console.error('Error fetching data:', error);
                if (this.filterStatus === 'dual_pension') {
                    this.errorMessage = 'Failed to load dual pensioners. Please try again.';
                } else {
                    this.errorMessage = 'Failed to load duplicates. Please try again.';
                }
                AuthService.CheckResponseError(error);
            } finally {
                this.isLoading = false;
            }
        },

        // Alias for backward compatibility
        fetchDuplicates() {
            this.fetchData();
        },

        /**
         * Handle filter change - refetch data when switching between duplicates and duals
         */
        onFilterChange() {
            // Only refetch from API when switching to/from dual_pension
            // Other filters (unresolved, resolved) are handled locally
            if (this.filterStatus === 'dual_pension' || this.previousFilter === 'dual_pension') {
                this.fetchData();
            }
            this.previousFilter = this.filterStatus;
        },

        /**
         * Transform Duals API response to component format
         * Duals API returns pensioners who have both State and LGA records
         */
        transformDualsData(data) {
            // Group by BVN since duals are pensioners with same BVN in both State and LGA
            const groups = {};
            
            data.forEach(p => {
                const key = p.bvn || p.nin || 'Unknown';
                if (!groups[key]) {
                    groups[key] = {
                        identifier: key,
                        status: 'valid',
                        isDualPension: true,
                        pensioners: []
                    };
                }
                groups[key].pensioners.push({
                    id: p.id || p.uuid,
                    pensionId: p.pensionId || p.uniquePensionerId || p.unique_pensioner_id,
                    firstName: p.firstName || p.first_name,
                    lastName: p.lastName || p.last_name,
                    bvn: p.bvn,
                    nin: p.nin,
                    phoneNo: p.phoneNo || p.phone_no,
                    photoLink: p.photoLink || p.photo_link,
                    isLga: p.isLga !== undefined ? p.isLga : p.is_lga,
                    monthlyPension: p.monthlyPension || p.monthly_pension,
                    status: p.status,
                    isPrimary: false
                });
            });
            
            // Only return groups with 2+ records (actual dual pensioners)
            return Object.values(groups).filter(g => g.pensioners.length >= 2);
        },

        /**
         * Transform API response to component's expected format
         * Adjust this based on actual API response structure
         */
        transformDuplicatesData(data) {
            // If API returns grouped data
            if (data.length > 0 && data[0].pensioners) {
                return data.map(group => ({
                    identifier: group.identifier || group.key || group[this.groupBy] || 'Unknown',
                    status: group.status || 'unresolved',
                    isDualPension: group.isDualPension || group.is_dual || this.checkIfDualPension(group.pensioners),
                    pensioners: group.pensioners.map(p => ({
                        id: p.id || p.uuid,
                        pensionId: p.pensionId || p.uniquePensionerId || p.unique_pensioner_id,
                        firstName: p.firstName || p.first_name,
                        lastName: p.lastName || p.last_name,
                        bvn: p.bvn,
                        nin: p.nin,
                        phoneNo: p.phoneNo || p.phone_no,
                        photoLink: p.photoLink || p.photo_link,
                        isLga: p.isLga !== undefined ? p.isLga : p.is_lga,
                        monthlyPension: p.monthlyPension || p.monthly_pension,
                        status: p.status,
                        isPrimary: p.isPrimary || p.is_primary || false
                    }))
                }));
            }
            
            // If API returns flat list, group by the selected field
            return this.groupDuplicatesBy(data, this.groupBy);
        },

        /**
         * Group flat list of pensioners by a field
         */
        groupDuplicatesBy(pensioners, field) {
            const groups = {};
            
            pensioners.forEach(p => {
                const key = p[field] || p[field.replace('_', '')] || 'Unknown';
                if (!groups[key]) {
                    groups[key] = {
                        identifier: key,
                        status: 'unresolved',
                        isDualPension: false,
                        pensioners: []
                    };
                }
                groups[key].pensioners.push({
                    id: p.id || p.uuid,
                    pensionId: p.pensionId || p.uniquePensionerId || p.unique_pensioner_id,
                    firstName: p.firstName || p.first_name,
                    lastName: p.lastName || p.last_name,
                    bvn: p.bvn,
                    nin: p.nin,
                    phoneNo: p.phoneNo || p.phone_no,
                    photoLink: p.photoLink || p.photo_link,
                    isLga: p.isLga !== undefined ? p.isLga : p.is_lga,
                    monthlyPension: p.monthlyPension || p.monthly_pension,
                    status: p.status,
                    isPrimary: false
                });
            });

            // Check if each group is a dual pension (has both State and LGA)
            Object.values(groups).forEach(group => {
                group.isDualPension = this.checkIfDualPension(group.pensioners);
            });

            return Object.values(groups);
        },

        /**
         * Check if a group represents valid dual pension (State + LGA)
         */
        checkIfDualPension(pensioners) {
            if (pensioners.length !== 2) return false;
            const hasState = pensioners.some(p => p.isLga === 0 || p.isLga === false);
            const hasLga = pensioners.some(p => p.isLga === 1 || p.isLga === true);
            return hasState && hasLga;
        },

        formatGroupBy(value) {
            const labels = {
                'bvn': 'BVN',
                'nin': 'NIN',
                'account_number': 'Account Number',
                'name': 'Name',
                'phone': 'Phone Number'
            };
            return labels[value] || value;
        },

        getHeaderClass(group) {
            if (group.isDualPension) {
                return 'header-dual-pension';
            } else if (group.status === 'resolved') {
                return 'header-resolved';
            }
            return 'header-needs-review';
        },

        getHeaderIcon(group) {
            if (group.isDualPension) {
                return 'fas fa-user-friends';
            } else if (group.status === 'resolved') {
                return 'fas fa-check-double';
            }
            return 'fas fa-search';
        },

        formatCurrency(amount) {
            if (!amount) return '0.00';
            return parseFloat(amount).toLocaleString('en-NG', { minimumFractionDigits: 2 });
        },

        getPhotoUrl(photoLink) {
            if (photoLink && photoLink !== null && photoLink !== '' && photoLink !== 'null') {
                return photoLink;
            }
            return this.imgDef;
        },

        handleImageError(event) {
            event.target.src = this.imgDef;
        },

        setPrimary(group, pensioner) {
            group.pensioners.forEach(p => p.isPrimary = false);
            pensioner.isPrimary = true;
        },

        markForRemoval(pensioner) {
            pensioner.markedForRemoval = true;
            // TODO: Implement API call
            alert('Record marked for removal (API not connected)');
        },

        markAsDualPension(group) {
            group.isDualPension = true;
            group.status = 'resolved';
            // TODO: Implement API call
            alert('Group marked as valid dual pension (API not connected)');
        },

        resolveGroup(group) {
            this.selectedGroup = group;
            this.showResolveModal = true;
        },

        confirmResolve() {
            if (this.selectedGroup) {
                this.selectedGroup.status = 'resolved';
                // TODO: Implement API call
                alert(`Duplicate resolved with note: ${this.resolutionNote} (API not connected)`);
            }
            this.showResolveModal = false;
            this.selectedGroup = null;
            this.resolutionNote = '';
        }
    }
};
</script>

<style scoped>
/* Card Header Styles - Calm, Professional Colors */
.header-needs-review {
    background: linear-gradient(135deg, #475569 0%, #64748b 100%);
    border-radius: 0.75rem 0.75rem 0 0;
}

.header-dual-pension {
    background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
    border-radius: 0.75rem 0.75rem 0 0;
}

.header-resolved {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    border-radius: 0.75rem 0.75rem 0 0;
}

/* Status Badges */
.badge-status {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 2rem;
    display: inline-flex;
    align-items: center;
}

.badge-review {
    background-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.badge-dual {
    background-color: rgba(255, 255, 255, 0.25);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.4);
}

.badge-resolved {
    background-color: rgba(255, 255, 255, 0.25);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.4);
}

/* Card Styling */
.card {
    border-radius: 0.75rem;
    overflow: hidden;
    border: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.avatar-sm {
    width: 32px;
    height: 32px;
}

.avatar-sm img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.btn-xs {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
}

.table-success {
    background-color: rgba(16, 185, 129, 0.08) !important;
}

/* Footer Actions */
.card-footer {
    border-top: 1px solid #e5e7eb;
}

.card-footer .btn {
    border-radius: 0.5rem;
}

.modal.show {
    background-color: rgba(0, 0, 0, 0.5);
}

/* Table Styling */
.table thead th {
    background-color: #f8fafc;
    border-bottom: 2px solid #e5e7eb;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
}

.table tbody tr:hover {
    background-color: #f8fafc;
}

/* Action Buttons */
.btn-link {
    padding: 0.25rem;
    border-radius: 0.375rem;
    transition: all 0.2s;
}

.btn-link:hover {
    background-color: #f1f5f9;
}

/* Footer Action Buttons */
.btn-dual-pension {
    background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
    color: #ffffff;
    border: none;
    font-weight: 500;
    padding: 0.5rem 1rem;
}

.btn-dual-pension:hover {
    background: linear-gradient(135deg, #0f766e 0%, #0d9488 100%);
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
}

.btn-resolve {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: #ffffff;
    border: none;
    font-weight: 500;
    padding: 0.5rem 1rem;
}

.btn-resolve:hover {
    background: linear-gradient(135deg, #047857 0%, #059669 100%);
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.btn-resolve:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.gap-2 {
    gap: 0.5rem;
}

/* Info Alert Styling */
.alert-info {
    background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
    border: 1px solid #7dd3fc;
    border-radius: 0.75rem;
    color: #0369a1;
}
</style>

